import {
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Link,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular, OpenRegular } from '@fluentui/react-icons';

import { FileIcon } from '@modules/items';

import { getComplianceCheckDocument } from '../constants';
import { TComplianceCheckIssue } from '../types';

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '760px',
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    paddingBottom: tokens.spacingVerticalXL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  sectionLabel: {
    color: tokens.colorNeutralForeground3,
  },
  documentList: {
    display: 'grid',
    gap: tokens.spacingVerticalS,
  },
  documentRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  documentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
    flex: '1 1 auto',
  },
  documentMeta: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  documentName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  documentLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
});

type TProps = {
  issue?: TComplianceCheckIssue;
  setIssue: (issue?: TComplianceCheckIssue) => void;
  getDocumentLinkHref: (documentId: string, issueId?: string) => string | undefined;
};

const ComplianceSuggestionDialog = ({ issue, setIssue, getDocumentLinkHref }: TProps) => {
  const classes = useStyles();

  return (
    <Dialog open={Boolean(issue)} onOpenChange={(_, data) => !data.open && setIssue(undefined)}>
      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                onClick={() => setIssue(undefined)}
              />
            }
          >
            View suggestion
          </DialogTitle>

          <DialogContent className={classes.dialogContent}>
            {issue && (
              <>
                <div className={classes.section}>
                  <Caption1 className={classes.sectionLabel}>Issue</Caption1>
                  <Body1>{issue.title}</Body1>
                </div>

                <div className={classes.section}>
                  <Caption1 className={classes.sectionLabel}>Affected documents</Caption1>
                  <div className={classes.documentList}>
                    {issue.documentIds.map((documentId) => {
                      const document = getComplianceCheckDocument(documentId);
                      if (!document) return null;

                      const documentLinkHref = getDocumentLinkHref(document.id, issue.id);

                      return (
                        <div key={document.id} className={classes.documentRow}>
                          <div className={classes.documentInfo}>
                            <FileIcon
                              type={document.itemType}
                              path={['Compliance check']}
                              isBlock={false}
                              isFrozen={false}
                              isTemplate={false}
                              mimetype={document.mimetype}
                              name={document.name}
                            />
                            <div className={classes.documentMeta}>
                              <Body1 className={classes.documentName} title={document.name}>
                                {document.name}
                              </Body1>
                              <Caption1 className={classes.sectionLabel}>{document.reviewScope}</Caption1>
                            </div>
                          </div>

                          {documentLinkHref ? (
                            <Link
                              href={documentLinkHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={classes.documentLink}
                            >
                              Open document
                              <OpenRegular />
                            </Link>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className={classes.section}>
                  <Caption1 className={classes.sectionLabel}>Recommended action</Caption1>
                  <Body1>{issue.recommendedAction}</Body1>
                </div>
              </>
            )}
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default ComplianceSuggestionDialog;
